import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { APP_ROUTES, endpoints } from "config";
import DeleteModal from "components/helpers/DeleteModal";

// Icons
import View from "assets/Icon_Action_01.png";
import Update from "assets/Icon_Action_02.png";
import Delete from "assets/Icon_Action_03.png";

import Category from "components/Category";
import { CrudItems } from "components/Category/CrudMenu";
import { lazyWithRetry } from "helpers/lazyWithRetry";
import { useDispatch, useSelector } from "react-redux";
import {
  clearResendVerificationLinkStatus,
  selectResendVerificationLinkStatus,
} from "reducers/auth";

import {
  CardListPage,
  NestedListItemProps,
  TCustomCell,
} from "components/Category/Category";
import AddBulk, { PAGES } from "pages/AddBulk/AddBulk";
import { parseListDate } from "helpers/parseListDate";
import { NestedFormData } from "components/Entity/Entity";
import { CustomRowCellColumns, selectCustomRowData } from "reducers/listPage";
import Roles from "pages/Roles";
import WorkflowConfigurations from "pages/WorkflowConfiguration/WorkflowConfigurations";
import {
  NestedFormModuleData,
  selectNestedFormModuleData,
} from "reducers/nestedCrudMenu";
import Projects from "pages/Projects";
import {
  clearSubmitResponseErrors,
  selectSubmitResponse,
} from "reducers/submitResponse";
import ToastMessage, { toastConfig } from "helpers/ToastMessage/ToastMessage";
import { toast } from "react-toastify";
import { Modal } from "components/modal/Modal";
import { ViewDocumentPopUp } from "components/newFlexTable/ViewDocumentCell";

const Workflow = lazyWithRetry(() => import("./Workflow"));

export type Data = {
  workflowInstances__ID: number;
  workflowInstances__Name: string;
  workflowInstances__Description: string;
  actionsApprovals__NextStep: string;
  actionsApprovals__Progress: string;
  workflowInstances__bActive: boolean;
  workflowInstances__AddDate: string;
  workflowInstances__AddedBy: string;
  workflowInstances__EditDate: string;
  workflowInstances__LastEditedBy: string;
  workflowInstances__CompletionDate: string;
  documents__ID: number;
};

// Define which column on the table represents each row's ID.
// This should be unique to each row.
export const idColumn = "workflowInstances__ID";

const getCrudItems = (
  nestedListItemProps?: NestedListItemProps,
  parentSelectedId?: number,
  parentSelectedIdName?: string,
  lowerCaseParentSelectedIdName?: string,
  nestedFormData?: NestedFormData,
  nestedFormDataComponent?: React.ReactNode
): CrudItems<Data> => {
  return [
    {
      icon: View,
      title: "Create workflow",
      imgAltText: "Create",
      menuCode: "CREATE",
      showInMultiSelect: false,
      permissionsKey: parentSelectedId ? "workflows-create" : "create",
      content: (onDone, selectedData, menu) => (
        <Workflow
          selectedData={selectedData}
          onDone={onDone}
          idFromRowOption={menu.selectedId}
          actionType="create"
          submitButtonText="Add"
          nestedListPageUrl={nestedListItemProps?.urls?.create ?? undefined}
          parentSelectedId={parentSelectedId ?? undefined}
          nestedFormGroupColumns={
            nestedFormData &&
            nestedFormData.groupColumns &&
            nestedFormData.groupColumns.create
              ? nestedFormData.groupColumns.create
              : undefined
          }
          createdDataSearchStringFieldName={
            nestedFormData && nestedFormData.createdDataSearchStringFieldName
              ? nestedFormData.createdDataSearchStringFieldName
              : undefined
          }
          createdDataSearchIdFieldName={
            nestedFormData && nestedFormData.createdDataSearchIdFieldName
              ? nestedFormData.createdDataSearchIdFieldName
              : undefined
          }
          nestedFormModuleName={
            nestedFormData ? APP_ROUTES.workflows : undefined
          }
          nestedFormDataComponent={nestedFormDataComponent}
        />
      ),
      inRightPanel: true,
      isDisabled: false,
      notInMenus: true,
    },
    {
      icon: View,
      title: "View workflow",
      imgAltText: "View",
      permissionsKey: parentSelectedId ? "workflows-view" : "view",
      menuCode: "VIEW",
      showInMultiSelect: false,
      content: (
        onDone,
        selectedData,
        menu,
        additionalData,
        clickedIdIfMultiIds
      ) => (
        <Workflow
          onDone={onDone}
          selectedData={selectedData}
          idFromRowOption={menu.selectedId}
          actionType="view"
          submitButtonText="Close"
          hideActionButton={true}
          nestedListPageUrl={nestedListItemProps?.urls?.view ?? undefined}
          parentSelectedId={parentSelectedId ?? undefined}
          autoMenuActionsCode={"VIEW"}
          clickedIdIfMultiIds={clickedIdIfMultiIds}
          nestedFormDataComponent={nestedFormDataComponent}
        />
      ),
      inRightPanel: true,
      isDisabled: false,
    },

    {
      icon: Delete,
      imgAltText: "Delete",
      permissionsKey: parentSelectedId ? "workflows-delete" : "delete",
      title: data =>
        data.length <= 1
          ? "Delete workflow"
          : `Delete ${data.length} workflows`,
      menuCode: "DELETE",
      showInMultiSelect: true,
      content: (onActionComplete, selectedData, menu) => (
        <DeleteModal
          url={nestedListItemProps?.urls?.delete ?? endpoints.workflows.delete}
          noun="workflow"
          selectedIds={selectedData.map(d => d.workflowInstances__ID)}
          renderListItem={data => {
            return (
              <li>{`${data.workflowInstances__ID} - ${data.workflowInstances__Name}`}</li>
            );
          }}
          selectedData={selectedData}
          open={menu.currentMenu === "DELETE"}
          close={onActionComplete}
          idFromRowOption={menu.selectedId}
          stripInfo={false}
          parentSelectedIdName={lowerCaseParentSelectedIdName ?? undefined}
          parentSelectedId={parentSelectedId ?? undefined}
        />
      ),
      inRightPanel: false,
      isDisabled: false,
    },
  ];
};

// Optional - function for formatting a row by column name. Takes a row and should
// spit out an object with the same keys.
// Should do this for date columns and format them as required
const rowFormatter = (
  row: Data
): { [K in keyof typeof row]: React.ReactNode } => {
  let formattedRow: any = { ...row };

  if (formattedRow.workflowInstances__AddDate) {
    formattedRow = {
      ...formattedRow,
      workflowInstances__AddDate: parseListDate(
        formattedRow.workflowInstances__AddDate,
        true
      ),
    };
  }
  if (formattedRow.workflowInstances__EditDate) {
    formattedRow = {
      ...formattedRow,
      workflowInstances__EditDate: parseListDate(
        formattedRow.workflowInstances__EditDate,
        true
      ),
    };
  }
  if (formattedRow.workflowInstances__CompletionDate) {
    formattedRow = {
      ...formattedRow,
      workflowInstances__CompletionDate: parseListDate(
        formattedRow.workflowInstances__CompletionDate,
        true
      ),
    };
  }
  return formattedRow;
};

export interface WorkflowsProps {
  parentSelectedId?: number;
  nestedListItemProps?: NestedListItemProps;
  parentSelectedIdName?: string;
  lowerCaseParentSelectedIdName?: string;
  hideCardPage?: boolean;
  nestedFormData?: NestedFormData;
}

export type Props = WorkflowsProps;

export type DisableMenuItemData = {
  [key: string]: { fieldName: string; fieldValue: any };
};

export const WORKFLOWS_PROGRESS_COLUMN = "actionsApprovals__Progress";
export const VIEW_DOCUMENT_COLUMN = "documents__ID";

const Workflows: React.FC<Props> = props => {
  const location = useLocation();

  const resendVerificationLinkStatus = useSelector(
    selectResendVerificationLinkStatus
  );

  const dispatch = useDispatch();

  let customRowCells: {
    [k in keyof Data]?: TCustomCell<Data>;
  } = {};

  customRowCells[CustomRowCellColumns.WORKFLOWS_PROGRESS] = {
    type: "ProgressBar",
    dataSource: "actionsApprovals__Progress",
    cutomCell: null,
  };

  useEffect(() => {
    if (
      resendVerificationLinkStatus ||
      resendVerificationLinkStatus === false
    ) {
      dispatch(clearResendVerificationLinkStatus());
    }
  }, [resendVerificationLinkStatus]);

  const relatedNestedFormData: NestedFormModuleData | null = useSelector(
    selectNestedFormModuleData
  );

  let nestedFormDataComponent: React.ReactNode | undefined = undefined;

  let nestedFormData: NestedFormData | undefined = undefined;
  if (relatedNestedFormData) {
    nestedFormData = {
      selectedId: relatedNestedFormData.selectedId,
      actionType: relatedNestedFormData.actionType,
      groupColumns: {
        create: {
          accesspermissions: 1,
          // accesspermissions: 2,
        },
      },
      createdDataSearchStringFieldName: {
        "/contacts": "contactName",
        "/projects": "projectsId", // Changed to "projectsId" as we no longer use project name anymore, changed logic to also check response for search string field name, not just our payload. We will search with the projectsId
        // "/projects": "projectsName",
      },
      createdDataSearchIdFieldName: {
        "/contacts": "id",
        "/projects": "projectsId",
      },
    };

    if (relatedNestedFormData.module === APP_ROUTES.workflowconfiguration) {
      nestedFormDataComponent = (
        <WorkflowConfigurations nestedFormData={nestedFormData} />
      );
    }
    if (relatedNestedFormData.module === APP_ROUTES.projects) {
      nestedFormDataComponent = <Projects nestedFormData={nestedFormData} />;
    }
  }

  const crudItems = getCrudItems(
    props.nestedListItemProps,
    props.parentSelectedId,
    props.parentSelectedIdName,
    props.lowerCaseParentSelectedIdName,
    props.nestedFormData,
    nestedFormDataComponent
  );

  // Define the url for the table's data endpoint.
  const url = props.nestedListItemProps?.urls?.list ?? endpoints.workflows.list;
  const csvUrl =
    props.nestedListItemProps &&
    props.nestedListItemProps.endpointDataToSend &&
    props.nestedListItemProps.endpointDataToSend.get &&
    props.nestedListItemProps.endpointDataToSend.get.listCsv &&
    props.nestedListItemProps.endpointDataToSend.get.listCsv.selectedParentId &&
    props.parentSelectedIdName &&
    props.nestedListItemProps?.urls?.listCsv
      ? `${props.nestedListItemProps?.urls?.listCsv}?${props.nestedListItemProps.endpointDataToSend.get.listCsv.selectedParentId}=${props.parentSelectedId}&bTemporaryFilters=true`
      : props.parentSelectedIdName && props.nestedListItemProps?.urls?.listCsv
      ? `${props.nestedListItemProps?.urls?.listCsv}?bTemporaryFilters=true`
      : `${endpoints.workflows.listCSV}?bTemporaryFilters=true`;

  return (
    <div className="WorkflowsPage CardListPage">
      <Category<Data>
        location={location}
        noun="workflow"
        createPermissionsKey={
          props.parentSelectedId ? "workflows-create" : "create"
        }
        addBulkPermissionsKey={
          props.parentSelectedId ? "workflows-upload" : "upload"
        }
        idColumn={idColumn}
        listEndpointUrl={url}
        defaultBActiveFilterColumnKey={"WorkflowInstances__bActive"}
        rowFormatter={rowFormatter}
        crudComponents={crudItems}
        hiddenColumns={[idColumn, "documents__ID"]}
        listCSV={{ url: csvUrl, noun: "Workflows" }}
        disableMenuItemData={{
          resendverificationlink: {
            fieldName: "siteUsers__bAccountVerified",
            fieldValue: true,
          },
        }}
        defaultCardOrListView={CardListPage.Card}
        hideHeaderTitle={props.parentSelectedId ? false : true}
        overridePageTitle={props.parentSelectedId ? "Workflows" : undefined}
        // showAddBulk={props.parentSelectedId ? undefined : "Upload workflows"}
        // showAddBulk={"Upload workflows"}
        cardProps={{
          cardTitleColumn: ["workflowInstances__Name"],
          cardTypeColumn: "actionsApprovals__Progress",
          includeTypeColumn: true,
          cardActionType: "view",
          overwriteCardNoun: "workflow",
          cardLayout: 6,
        }}
        largeRightPanel={
          // false
          action => action === "VIEW"
          // action === "CREATE" || action === "EDIT" || action === "VIEW"
        }
        rowCellOptions={{
          workflowDetails__WorkflowsName: { width: "SemiLarge3" },
          workflowDetails__PhoneNumber: { width: "SemiLarge3" },
          workflowDetails__EmailAddress: { width: "SemiLarge3" },
          workflowTypes__WorkflowTypesName: { width: "SemiLarge2" },
          workflowAccessTypes__WorkflowAccessTypesName: { width: "Large" },
          workflows__StateProvince: { width: "SemiLarge3" },
          workflows__PostalZipCode: { width: "SemiLarge3" },
          workflows__Longitude: { width: "SemiLarge" },
          workflows__AddDate: { width: "SemiLarge" },
          workflows__AddedBy: { width: "SemiLarge" },
          sites__SitesName: { width: "Large2" },
          projects__ProjectsName: { width: "SemiLarge2" },
          platformUsers__Name: { width: "SemiLarge3" },
          workflows__EditDate: { width: "SemiLarge" },
          workflows__LastEditedBy: { width: "SemiLarge2" },
          WorkflowInstances__ID: { width: "Small" },
          workflowInstances__ID: { width: "Small" },
          sites__Id: { width: "SemiSmall" },
          // sites__Id: { width: "Medium" },
        }}
        leftTableReducedPadding={true}
        nestedListItemProps={props.nestedListItemProps}
        parentSelectedId={props.parentSelectedId}
        hideCardPage={props.hideCardPage}
        nestedFormData={props.nestedFormData}
        customRowCells={customRowCells}
        // maxCardPageTitleLength={20}
        centerCardPageTitle={true}
        customRowCellColumn={CustomRowCellColumns.VIEW_DOCUMENT}
        rowButton={data => <ViewDocument data={data} {...props} />}
        hideRowOptionsMenuComponent={true}
        showFilterHeaderButton={true}
        hideFilterTableButton={true}
        showRightTable
      />
      {resendVerificationLinkStatus && (
        <ToastMessage
          toastData={[{ title: "Success", text: "Verification link sent" }]}
          className="ToastSuccess"
        />
      )}

      {resendVerificationLinkStatus === false && (
        <ToastMessage
          toastData={[
            { title: "Error", text: "Failed to send verification link" },
          ]}
          className="ToastError"
        />
      )}
    </div>
  );
};

export default Workflows;

const ViewDocument = (props: Props & { data: any }) => {
  const [showViewDocumentPopUp, setShowViewDocumentPopUp] = useState<boolean>(
    false
  );

  const { data } = props;

  const dispatch = useDispatch();

  const finalValue = data && data.documents__ID ? data.documents__ID : null;

  // console.log("props: ", props);

  // console.log("data.documents__ID: ", data.documents__ID);
  // console.log("finalValue: ", finalValue);

  const submitResponseErrors = useSelector(selectSubmitResponse)
    .submitResponseErrors;
  const hasMounted = useRef<boolean>(false);

  useEffect(() => {
    if (submitResponseErrors && hasMounted.current) {
      toast(
        <ToastMessage
          toastData={Object.entries(submitResponseErrors).map(
            ([title, text]: [string, any]) => ({
              title,
              text,
            })
          )}
          className="ToastError"
        />,
        {
          ...toastConfig,
        }
      );
    }
    if (!hasMounted.current) {
      dispatch(clearSubmitResponseErrors());
      hasMounted.current = true;
    }
  }, [submitResponseErrors]);

  return (
    <>
      <div
        className={`ViewDocumentBtn Btn ${!finalValue ? "Disabled" : ""}`}
        onClick={e => {
          e.stopPropagation();

          if (finalValue) {
            setShowViewDocumentPopUp(true);
          }
        }}
      >
        View
      </div>

      {showViewDocumentPopUp && finalValue && (
        <Modal
          open={showViewDocumentPopUp}
          closeModal={() => setShowViewDocumentPopUp(false)}
          title={`View Document`}
          // actionButton={
          //   <button className="Btn BtnRed" onClick={handleDelete}>
          //     Delete
          //   </button>
          // }
        >
          <ViewDocumentPopUp
            id={finalValue}
            onDone={() => setShowViewDocumentPopUp(false)}
          />
        </Modal>
      )}
    </>
  );
};
